<template>
  <DashboardLayout :lastUpdated="lastUpdated">
    <template slot="navbarFilters">
      <div v-if="hasWorldStore" class="navbar-filters">
        <div v-if="sellerAccountsList && sellerAccountsList.length > 0" class="ui dropdown filter-dropdown navbar-dropdown" ref="saDropdown">
          <div class="dropdown-trigger">
            <span class="text">S.Accounts: {{ filterBySellAcc.length === 0 ? 'All' : filterBySellAcc.length + ' selected' }}</span>
            <i class="dropdown icon"></i>
          </div>
          <div class="menu">
            <div class="item" :class="{ selected: filterBySellAcc.length === 0 }" data-value="">
              <i class="check icon" v-if="filterBySellAcc.length === 0"></i>
              All
            </div>
            <div class="divider"></div>
            <div v-for="sa in sellerAccountsList" :key="String(sa.sellerId)" class="item" :class="{ selected: isSellerSelected(sa.sellerId) }" :data-value="sa.sellerId">
              <i class="check icon" v-if="isSellerSelected(sa.sellerId)"></i>
              {{ sa.accountNickName }}
            </div>
          </div>
        </div>
        <div v-if="filterUserCountries && filterUserCountries.length > 0" class="ui dropdown filter-dropdown navbar-dropdown" ref="marketplaceDropdown">
          <div class="dropdown-trigger">
            <span class="text">Marketplaces: {{ filterByMarket.length === 0 ? 'All' : filterByMarket.length === 1 ? filterByMarket[0] : filterByMarket.length + ' selected' }}</span>
            <i class="dropdown icon"></i>
          </div>
          <div class="menu">
            <div class="item" :class="{ selected: filterByMarket.length === 0 }" data-value="">
              <i class="check icon" v-if="filterByMarket.length === 0"></i>
              All
            </div>
            <div class="divider"></div>
            <div v-for="c in filteredCountries" :key="c" :class="[c === 's' ? 'divider' : 'item', { selected: filterByMarket.indexOf(c) >= 0 && c !== 's' }]" :data-value="c">
              <i class="check icon" v-if="filterByMarket.indexOf(c) >= 0 && c !== 's'"></i>
              <i v-if="c !== 's'" :class="c.toLowerCase().replace('uk', 'gb') + ' flag'" style="margin-right: 8px;"></i>
              <span v-if="c !== 's'">Amazon.{{ getMarketplaceSuffix(c) }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div class="buybox-dashboard">
      <div class="dashboard-content">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <i class="spinner loading icon"></i>
        <p>Loading dashboard data...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <i class="exclamation triangle icon"></i>
        <h3>Failed to load dashboard</h3>
        <p>{{ error }}</p>
        <button class="ui primary button" @click="fetchDashboardData">Retry</button>
      </div>

      <!-- Dashboard Content -->
      <template v-else>
        <!-- Skeleton overlay when syncing -->
        <div class="dashboard-stats">
          <div v-if="!onboardingComplete" class="stat-card ownership-card sk-skeleton-card">
            <div class="stat-header">
              <span class="stat-title">BUY BOX OWNERSHIP <i class="info circle icon"></i></span>
            </div>
            <div class="sk-skel-block sk-skel-lg"></div>
            <div class="sk-skel-block sk-skel-sm" style="width:70%"></div>
            <div class="sk-skel-pill">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
              Reading Buy Box status&hellip;
            </div>
          </div>
          <BuyBoxOwnershipCard v-else :data="ownership" />

          <div v-if="!onboardingComplete" class="stat-card revenue-card sk-skeleton-card">
            <div class="stat-header revenue-header">
              <div class="header-left">
                <span class="stat-title">EST. DAILY REVENUE IMPACT <i class="info circle icon"></i></span>
              </div>
              <span class="stat-tag">ESTIMATE</span>
            </div>
            <div class="sk-skel-block sk-skel-lg"></div>
            <div class="sk-skel-block sk-skel-sm" style="width:85%"></div>
            <div class="sk-skel-block-wide"></div>
            <div class="sk-skel-pill">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
              Awaiting Buy Box &amp; sales data&hellip;
            </div>
          </div>
          <RevenueImpactCard v-else :data="revenueImpact" />
        </div>

        <div v-if="!onboardingComplete" class="sk-skeleton-section">
          <div class="sk-section-label">BUY BOX STATUS BREAKDOWN</div>
          <div class="sk-status-grid">
            <div class="sk-status-placeholder" v-for="n in 4" :key="n">
              <div class="sk-skel-block sk-skel-sm" style="width:40%"></div>
              <div class="sk-skel-block sk-skel-xs" style="width:60%"></div>
            </div>
          </div>
        </div>
        <StatusBreakdown v-else
          :statusData="statusBreakdown" 
          :activeType="activeStatusFilter"
          @status-click="handleStatusFilter"
        />

        <PriorityIssuesList
          :issues="onboardingComplete ? priorityIssues : []"
          :needActionCount="needActionCount"
          :totalIssues="totalIssues"
          :syncing="!onboardingComplete"
          :exporting="exporting"
          @viewAll="viewAllAlerts"
          @export="exportPriorityIssuesCsv"
          @product-click="viewProductDetails"
          @action-click="viewProductDetails"
        />
      </template>
      </div>
    </div>
  </DashboardLayout>
</template>

<script>
import $ from 'jquery'
import { extractV1SellersItems, mapV1SellersToFilterAccounts, extractMarketplaceCodesFromV1SellersBody, buildMarketplaceFilterCodes, buildMarketplaceFilterDropdownItems, MARKETPLACE_FILTER_COUNTRIES_ALL, getMarketplaceAmazonSuffix } from '../utils/sellerAccountsFromV1Api.js'
import './stock/styles/StockDashboard.css'
import './buybox/styles/BuyBoxDashboard.css'
import DashboardLayout from './buybox/DashboardLayout.vue'
import BuyBoxOwnershipCard from './buybox/BuyBoxOwnershipCard.vue'
import RevenueImpactCard from './buybox/RevenueImpactCard.vue'
import StatusBreakdown from './buybox/StatusBreakdown.vue'
import PriorityIssuesList from './buybox/PriorityIssuesList.vue'
import { buildProductDetailsQuery } from '../utils/listViewUrlSync.js'
import { createRequestGate, isRequestAborted } from '../utils/requestGate.js'

const DASH_URL_KEYS = ['marketplaces', 'sellerAccounts', 'status']

function shallowEqualDashQuery (next, routeQuery) {
  for (var i = 0; i < DASH_URL_KEYS.length; i++) {
    var k = DASH_URL_KEYS[i]
    var nv = next[k] != null ? String(next[k]) : ''
    var rv = routeQuery && routeQuery[k] != null ? String(routeQuery[k]) : ''
    if (nv !== rv) return false
  }
  return true
}

export default {
  name: 'BuyBoxDashboard',
  components: {
    DashboardLayout,
    BuyBoxOwnershipCard,
    RevenueImpactCard,
    StatusBreakdown,
    PriorityIssuesList
  },
  data: function () {
    return {
      loading: true,
      error: null,
      lastUpdated: 'just now',
      activeTab: 'issues',
      timerInterval: null,
      refreshInterval: null,
      // Data from API
      apiData: null,
      // Default values
      ownership: {
        percentage: 0,
        owned: 0,
        total: 0,
        trend: null,
        trendText: '',
        source: 'SP-API',
        field: 'buybox_ownership',
        calculation: '(owned_count / total_count) * 100',
        trendDescription: 'Compare with previous day'
      },
      revenueImpact: {
        amount: '0',
        affectedProducts: 0,
        weeklyImpact: '-$0',
        actionableProducts: 0,
        avgRecovery: 'N/A',
        reportingCurrencySymbol: '$'
      },
      statusBreakdown: [],
      priorityIssues: [],
      needActionCount: 0,
      totalIssues: 0,
      activeStatusFilter: 'suppressed',
      dataSourceName: 'Unknown',
      productsTracked: 0,
      notificationsToday: 0,
      filterByMarket: [],
      filterBySellAcc: [],
      filterCountriesAll: MARKETPLACE_FILTER_COUNTRIES_ALL,
      filterUserCountries: [],
      buyboxFilterData: { availableFilters: null },
      loadingFilters: false,
      exporting: false,
      _syncingDropdowns: false,
      _applyingDashRouteQuery: false,
      _dashFetchDebounce: null,
      _dashRequestGate: null
    }
  },
  computed: {
    worldStore: function () {
      return this.$parent && this.$parent.$parent && this.$parent.$parent.worldStore
    },
    hasWorldStore: function () {
      return !!(this.worldStore && this.worldStore.products !== undefined)
    },
    sellerAccountsList: function () {
      var af = this.buyboxFilterData && this.buyboxFilterData.availableFilters
      if (!af || !af.sellerAccounts || af.sellerAccounts.length === 0) return []
      return af.sellerAccounts.map(function (sa) {
        return { sellerId: sa.id, accountNickName: sa.name }
      })
    },
    filteredCountries: function () {
      return buildMarketplaceFilterDropdownItems(this.filterUserCountries, this.filterCountriesAll)
    },
    /** Index shell layout (provides onboarding polling). Prefer $root ref from IndexPage; fall back to $parent walk. */
    indexShell: function () {
      var root = this.$root
      if (root && root._skIndexShellForDashboard && typeof root._skIndexShellForDashboard.fetchOnboardingStatus === 'function') {
        return root._skIndexShellForDashboard
      }
      var p = this.$parent
      while (p) {
        if (typeof p.fetchOnboardingStatus === 'function') return p
        p = p.$parent
      }
      return null
    },
    onboardingStatus: function () {
      var shell = this.indexShell
      return shell && shell.onboardingStatus ? shell.onboardingStatus : null
    },
    indexOnboardingReady: function () {
      var shell = this.indexShell
      return !!(shell && shell.onboardingStatusReady)
    },
    onboardingComplete: function () {
      var s = this.onboardingStatus
      if (!s) return true
      return !!s.completed ||
        (!!s.sellerConnected && !!s.productsImported && !!s.buyboxFetched && !!s.ordersFetched)
    },
    syncPercent: function () {
      if (!this.onboardingStatus) return 0
      var s = this.onboardingStatus
      var done = 0
      if (s.sellerConnected) done++
      if (s.productsImported) done++
      if (s.buyboxFetched) done++
      if (s.ordersFetched) done++
      return Math.round((done / 4) * 100)
    },
    syncStatusMessage: function () {
      if (!this.onboardingStatus) return 'Loading…'
      var s = this.onboardingStatus
      if (!s.sellerConnected) return 'Waiting for Amazon account connection…'
      if (!s.productsImported) return 'Products loaded — importing catalog and ASINs…'
      if (!s.buyboxFetched) return 'Products loaded — Buy Box data and revenue estimates are still coming in.'
      if (!s.ordersFetched) return 'Buy Box data loaded — fetching order history…'
      return 'Finishing up…'
    }
  },
  created: function () {
    this.applyDashboardRouteQuery(this.$route.query)
  },
  mounted: function () {
    this.startLiveTimer()
    this.loadBuyboxFilters()
    var self = this
    this.$nextTick(function () {
      self.initializeFilterDropdowns()
    })
    this.fetchDashboardData()
    this.$nextTick(function () {
      self.syncDashboardRouteQuery()
    })
  },
  beforeDestroy: function () {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
    if (this._dashFetchDebounce) {
      clearTimeout(this._dashFetchDebounce)
      this._dashFetchDebounce = null
    }
  },
  methods: {
    scheduleDashboardFetch: function () {
      var self = this
      if (self._dashFetchDebounce) clearTimeout(self._dashFetchDebounce)
      self._dashFetchDebounce = setTimeout(function () {
        self._dashFetchDebounce = null
        self.fetchDashboardData()
      }, 300)
    },
    dashboardFilterParams: function () {
      var params = {}
      if (this.filterByMarket.length > 0) params.marketplaces = this.filterByMarket.join(',')
      if (this.filterBySellAcc.length > 0) params.sellerAccounts = this.filterBySellAcc.join(',')
      return params
    },
    exportPriorityIssuesCsv: function () {
      if (this.exporting) return
      var self = this
      var params = Object.assign({}, this.dashboardFilterParams())
      if (this.activeStatusFilter) params.status = this.activeStatusFilter
      params.sortBy = 'impact'

      var qs = Object.keys(params).map(function (key) {
        return encodeURIComponent(key) + '=' + encodeURIComponent(params[key])
      }).join('&')
      var exportUrl = '/api/v1/buybox/dashboard/issues/export' + (qs ? '?' + qs : '')
      var defaultName = 'buybox-issues-' + new Date().toISOString().slice(0, 10) + '.csv'

      this.exporting = true
      var xhr = new XMLHttpRequest()
      xhr.open('GET', exportUrl, true)
      xhr.responseType = 'blob'
      xhr.onload = function () {
        self.exporting = false
        if (xhr.status === 200) {
          var blob = xhr.response
          var url = window.URL.createObjectURL(blob)
          var link = document.createElement('a')
          link.href = url
          var cd = xhr.getResponseHeader('Content-Disposition')
          var filename = defaultName
          if (cd) {
            var m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (m && m[1]) filename = m[1].replace(/['"]/g, '').trim() || defaultName
          }
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        } else {
          var reader = new FileReader()
          reader.onload = function () {
            var errorMsg = 'Failed to export issues. Please try again.'
            try {
              var errorJson = JSON.parse(reader.result)
              errorMsg = errorJson.message || errorJson.error || errorMsg
            } catch (e) { /* not JSON */ }
            alert(errorMsg)
          }
          reader.readAsText(xhr.response)
        }
      }
      xhr.onerror = function () {
        self.exporting = false
        alert('Failed to export issues. Please try again.')
      }
      xhr.send()
    },
    getMarketplaceSuffix: function (country) {
      return getMarketplaceAmazonSuffix(country)
    },
    isSellerSelected: function (sellerId) {
      if (!this.filterBySellAcc || this.filterBySellAcc.length === 0) return false
      return this.filterBySellAcc.some(function (id) { return String(id) === String(sellerId) })
    },
    loadBuyboxFilters: function () {
      var self = this
      this.loadingFilters = true
      var sellersPromise = self.$root.loadV1Sellers
        ? self.$root.loadV1Sellers().then(function (body) { return body }, function () { return null })
        : self.$http.get('/api/v1/sellers').then(function (res) { return res.body || {} }, function () { return null })
      sellersPromise.then(function (sellersBody) {
        self.loadingFilters = false
        sellersBody = sellersBody || {}
        var sellerAccounts = mapV1SellersToFilterAccounts(extractV1SellersItems(sellersBody)).map(function (r) {
          return { id: r.id, name: r.name }
        })
        var marketplaces = extractMarketplaceCodesFromV1SellersBody(sellersBody)
        self.buyboxFilterData.availableFilters = { marketplaces: marketplaces, sellerAccounts: sellerAccounts }
        self.filterUserCountries = buildMarketplaceFilterCodes({
          fromAvailableFilters: marketplaces,
          v1SellersBody: sellersBody,
          canonicalOrder: self.filterCountriesAll.filter(function (c) { return c !== 's' })
        })
        if (self.filterByMarket.length > 0) {
          var mpSet = new Set(self.filterUserCountries)
          self.filterByMarket = self.filterByMarket.filter(function (x) { return mpSet.has(x) })
        }
        if (sellerAccounts.length > 0 && self.filterBySellAcc.length > 0) {
          var idSet = new Set(sellerAccounts.map(function (sa) { return String(sa.id) }))
          self.filterBySellAcc = self.filterBySellAcc.filter(function (id) { return idSet.has(String(id)) })
        }
        self.$nextTick(function () {
          self.initializeFilterDropdowns()
        })
      }, function () {
        self.loadingFilters = false
        self.buyboxFilterData.availableFilters = null
        self.filterUserCountries = []
      })
    },
    initializeFilterDropdowns: function () {
      var self = this
      if (this.$refs.saDropdown) {
        try { $(this.$refs.saDropdown).dropdown('destroy') } catch (e) { /* not yet initialized */ }
        $(this.$refs.saDropdown).dropdown({
          onChange: function (v) {
            if (self._syncingDropdowns) return
            self.filterBySellAcc = v === '' ? [] : v.split(',').map(Number)
            self.scheduleDashboardFetch()
            self.syncDashboardRouteQuery()
          },
          placeholder: 'All Accounts',
          fullTextSearch: true
        })
      }
      if (this.$refs.marketplaceDropdown) {
        try { $(this.$refs.marketplaceDropdown).dropdown('destroy') } catch (e) { /* not yet initialized */ }
        $(this.$refs.marketplaceDropdown).dropdown({
          onChange: function (v) {
            if (self._syncingDropdowns) return
            self.filterByMarket = v === '' ? [] : v.split(',')
            self.scheduleDashboardFetch()
            self.syncDashboardRouteQuery()
          },
          placeholder: 'All Marketplaces',
          fullTextSearch: true
        })
      }
    },
    fetchDashboardData: function () {
      var self = this
      if (!self._dashRequestGate) self._dashRequestGate = createRequestGate()
      var gate = self._dashRequestGate
      var gen = gate.begin()
      self.loading = true
      self.error = null

      var params = Object.assign({}, self.dashboardFilterParams())

      // Priority issues in parallel — don't wait for dashboard cards.
      self.fetchPriorityIssues({
        page: 0,
        pageSize: 5,
        sortBy: 'impact',
        status: self.activeStatusFilter || null,
        _gen: gen
      })

      self.$http.get('/api/v1/buybox/dashboard', gate.trackOpts({ params: params })).then(function (response) {
        if (!gate.isCurrent(gen)) return
        if (response.body) {
          self.apiData = response.body
          self.mapApiDataToProps(response.body)
          self.updateLastUpdated(response.body.generated_at || response.body.data_source && response.body.data_source.last_updated)
        }
        self.loading = false
      }, function (err) {
        if (!gate.isCurrent(gen) || isRequestAborted(err)) return
        console.error('[BuyBoxDashboard] Failed to fetch dashboard data:', err)
        var errorMsg = 'Failed to load dashboard data'
        if (err.body && err.body.message) {
          errorMsg = err.body.message
        } else if (err.message) {
          errorMsg = err.message
        }
        self.error = errorMsg
        self.loading = false
      })
    },

    mapApiDataToProps: function (data) {
      var self = this
      var reportingSym =
        data.reporting_currency_symbol ||
        data.reportingCurrencySymbol ||
        (data.data_source && (data.data_source.reporting_currency_symbol || data.data_source.reportingCurrencySymbol)) ||
        '$'

      // Map buybox_ownership
      if (data.buybox_ownership) {
        var bo = data.buybox_ownership
        this.ownership = {
          percentage: bo.percentage || 0,
          owned: bo.owned_count || 0,
          total: bo.total_count || 0,
          trend: bo.trend,
          trendText: bo.trend || '',
          source: 'SQS Push + SP-API',
          field: 'buybox_ownership',
          calculation: '(owned_count / total_count) * 100',
          trendDescription: bo.subtitle || ''
        }
      }

      // Map revenue_impact
      if (data.revenue_impact) {
        var ri = data.revenue_impact
        this.revenueImpact = {
          amount: ri.estimated_daily_loss_dollars !== undefined ? String(ri.estimated_daily_loss_dollars) : '0',
          affectedProducts: ri.actionable && ri.actionable.count !== undefined ? ri.actionable.count : 0,
          weeklyImpact: ri.this_week && ri.this_week.display ? ri.this_week.display : ('-' + reportingSym + '0'),
          actionableProducts: ri.actionable && ri.actionable.count !== undefined ? ri.actionable.count : 0,
          avgRecovery: ri.avg_recovery && ri.avg_recovery.display ? ri.avg_recovery.display : 'N/A',
          subtitle: ri.subtitle || null,
          reportingCurrencySymbol: ri.reporting_currency_symbol || ri.reportingCurrencySymbol || null
        }
      } else {
        this.revenueImpact = Object.assign({}, this.revenueImpact, { reportingCurrencySymbol: reportingSym })
      }

      // Map status_breakdown
      if (data.status_breakdown) {
        var sb = data.status_breakdown
        var statusArray = []

        if (sb.suppressed) {
          statusArray.push({
            type: 'suppressed',
            icon: 'ban icon',
            count: sb.suppressed.count || 0,
            label: sb.suppressed.label || 'Suppressed',
            description: sb.suppressed.description || ''
          })
        }

        if (sb.lost_to_competitor) {
          statusArray.push({
            type: 'lost',
            icon: 'arrow down icon',
            count: sb.lost_to_competitor.count || 0,
            label: sb.lost_to_competitor.label || 'Lost to Competitor',
            description: sb.lost_to_competitor.description || ''
          })
        }

        if (sb.contested) {
          statusArray.push({
            type: 'contested',
            icon: 'sync alternate icon',
            count: sb.contested.count || 0,
            label: sb.contested.label || 'Contested',
            description: sb.contested.description || ''
          })
        }

        if (sb.owned) {
          statusArray.push({
            type: 'owned',
            icon: 'check circle icon',
            count: sb.owned.count || 0,
            label: sb.owned.label || 'Owned',
            description: sb.owned.description || ''
          })
        }

        this.statusBreakdown = statusArray

        var suppressedCount = sb.suppressed && sb.suppressed.count != null ? sb.suppressed.count : 0
        var lostCount = sb.lost_to_competitor && sb.lost_to_competitor.count != null ? sb.lost_to_competitor.count : 0
        var contestedCount = sb.contested && sb.contested.count != null ? sb.contested.count : 0
        this.needActionCount = suppressedCount + lostCount + contestedCount
      }

      // priority_issues come from dedicated /issues fetch (parallel) — skip embedded copy

      // Map data_source
      if (data.data_source) {
        var ds = data.data_source
        this.dataSourceName = ds.source || 'Unknown'
        this.productsTracked = ds.products_tracked || 0
        this.notificationsToday = ds.notifications_today || 0
      }
    },

    mapPriorityIssuesFromApi: function (pi) {
      var self = this

      if (!pi) {
        this.totalIssues = 0
        this.priorityIssues = []
        return
      }

      this.totalIssues = pi.total_count || 0

      if (pi.items && pi.items.length > 0) {
        this.priorityIssues = pi.items.map(function (item) {
          var statusType = self.mapStatusType(item.status)
          var impactDisplay = item.estimated_daily_loss_display || '-$0'
          if (statusType === 'owned' && impactDisplay && String(impactDisplay).indexOf('-') === 0) {
            impactDisplay = String(impactDisplay).replace(/^-/, '').trim() || '$0'
          }
          return {
            product: item.name || item.asin || 'Unknown Product',
            status: item.status_label || item.status || '',
            statusType: statusType,
            confidenceLevel: item.confidence && item.confidence.level ? item.confidence.level : 'medium',
            description: item.issue_description || '',
            badge: item.confidence && item.confidence.label ? item.confidence.label.split(' - ')[0] : null,
            badgeClass: self.mapBadgeClass(item.confidence && item.confidence.level),
            impact: impactDisplay,
            impactClass: self.getImpactClass(item.estimated_daily_loss_display, statusType),
            actionButton: item.needs_action ? (item.action_label || 'View') : null,
            actionButtonClass: item.action_type === 'analyze' ? 'secondary' : 'primary',
            productId: item.product_id,
            asin: item.asin,
            imageUrl: item.image_url,
            ownPrice: item.own_price_display,
            competitorPrice: item.competitor_price_display,
            priceGap: item.price_gap_display
          }
        })
      } else {
        this.priorityIssues = []
      }
    },

    fetchPriorityIssues: function (options) {
      var self = this
      if (!self._dashRequestGate) self._dashRequestGate = createRequestGate()
      var gate = self._dashRequestGate
      var gen = options && options._gen != null ? options._gen : gate.begin()
      var params = Object.assign({}, self.dashboardFilterParams(), {
        page: options && options.page !== undefined ? options.page : 0,
        pageSize: options && options.pageSize !== undefined ? options.pageSize : 5,
        sortBy: options && options.sortBy ? options.sortBy : 'impact'
      })
      if (options && options.status) {
        params.status = options.status
      }

      this.$http.get('/api/v1/buybox/dashboard/issues', gate.trackOpts({ params: params })).then(function (response) {
        if (!gate.isCurrent(gen)) return
        if (!response.body) return
        var body = response.body
        var pi = body.priority_issues || body
        self.mapPriorityIssuesFromApi(pi)
      }, function (err) {
        if (!gate.isCurrent(gen) || isRequestAborted(err)) return
        console.error('[BuyBoxDashboard] Failed to fetch priority issues:', err)
      })
    },

    handleStatusFilter: function (statusType) {
      if (this.activeStatusFilter === statusType) {
        this.activeStatusFilter = ''
      } else {
        this.activeStatusFilter = statusType
      }

      this.fetchPriorityIssues({
        page: 0,
        pageSize: 5,
        sortBy: 'impact',
        status: this.activeStatusFilter || null
      })
      this.syncDashboardRouteQuery()
    },

    mapStatusType: function (status) {
      if (!status) return 'lost'
      var statusLower = status.toLowerCase()
      if (statusLower.indexOf('suppress') >= 0) return 'suppressed'
      if (statusLower.indexOf('lost') >= 0 || statusLower.indexOf('competitor') >= 0) return 'lost'
      if (statusLower.indexOf('contest') >= 0) return 'contested'
      if (statusLower.indexOf('owned') >= 0 || statusLower.indexOf('win') >= 0) return 'owned'
      return 'lost'
    },

    mapBadgeClass: function (level) {
      if (!level) return 'info'
      if (level === 'high') return 'warning'
      if (level === 'medium') return 'info'
      if (level === 'low') return 'secondary'
      return 'info'
    },

    getImpactClass: function (display, statusType) {
      if (statusType === 'owned') return 'neutral'
      if (!display) return 'negative'
      var str = String(display).trim()
      if (str.startsWith('+')) return 'positive'
      return 'negative'
    },

    updateLastUpdated: function (timestamp) {
      if (timestamp) {
        this.lastUpdated = 'just now'
      }
    },

    startLiveTimer: function () {
      var self = this
      var startTime = Date.now()

      this.timerInterval = setInterval(function () {
        var elapsed = Math.floor((Date.now() - startTime) / 1000)

        if (elapsed < 60) {
          self.lastUpdated = elapsed + 's ago'
        } else {
          var minutes = Math.floor(elapsed / 60)
          self.lastUpdated = minutes + 'm ago'
        }
      }, 1000)
    },

    toggleUserMenu: function () {
      console.log('User menu toggled')
    },

    viewAllAlerts: function () {
      var q = {}
      if (this.filterByMarket.length) q.marketplaces = this.filterByMarket.join(',')
      if (this.filterBySellAcc.length) q.sellerAccounts = this.filterBySellAcc.map(String).join(',')
      this.$router.push({ path: '/all-alerts', query: q })
    },

    buildDashboardRouteQuery: function () {
      var o = {}
      if (this.filterByMarket.length) o.marketplaces = this.filterByMarket.join(',')
      if (this.filterBySellAcc.length) o.sellerAccounts = this.filterBySellAcc.map(String).join(',')
      if (this.activeStatusFilter) o.status = this.activeStatusFilter
      return o
    },
    syncDashboardRouteQuery: function () {
      if (this._applyingDashRouteQuery || this.$route.path !== '/home') return
      var next = this.buildDashboardRouteQuery()
      if (shallowEqualDashQuery(next, this.$route.query)) return
      this.$router.replace({ path: '/home', query: next })
    },
    applyDashboardRouteQuery: function (q) {
      if (!q || typeof q !== 'object') return false
      var has = false
      for (var i = 0; i < DASH_URL_KEYS.length; i++) {
        var k = DASH_URL_KEYS[i]
        if (q[k] != null && q[k] !== '') {
          has = true
          break
        }
      }
      if (!has) return false

      this._applyingDashRouteQuery = true
      try {
        if (!Object.prototype.hasOwnProperty.call(q, 'marketplaces')) this.filterByMarket = []
        if (!Object.prototype.hasOwnProperty.call(q, 'sellerAccounts')) this.filterBySellAcc = []
        if (!Object.prototype.hasOwnProperty.call(q, 'status')) this.activeStatusFilter = ''

        if (Object.prototype.hasOwnProperty.call(q, 'marketplaces') && q.marketplaces) {
          this.filterByMarket = String(q.marketplaces).split(',').map(function (s) { return s.trim() }).filter(Boolean)
        }
        if (Object.prototype.hasOwnProperty.call(q, 'sellerAccounts') && q.sellerAccounts) {
          this.filterBySellAcc = String(q.sellerAccounts).split(',').map(function (s) {
            var t = s.trim()
            if (!t) return null
            var n = Number(t)
            return isNaN(n) ? t : n
          }).filter(function (id) { return id != null && id !== '' })
        }
        if (Object.prototype.hasOwnProperty.call(q, 'status')) {
          this.activeStatusFilter = q.status ? String(q.status) : ''
        }
      } finally {
        var self = this
        this.$nextTick(function () {
          self._applyingDashRouteQuery = false
        })
      }
      return true
    },

    viewProductDetails: function (issue) {
      if (issue && issue.productId) {
        this.syncDashboardRouteQuery()
        this.$router.push({
          path: '/product_details',
          query: buildProductDetailsQuery(issue.productId, this.$route.fullPath)
        })
      }
    },
  },
  watch: {
    '$route': function (to, from) {
      if (to.path !== '/home') return
      if (this._applyingDashRouteQuery) return
      if (from && from.path === to.path && shallowEqualDashQuery(this.buildDashboardRouteQuery(), to.query)) return
      this.applyDashboardRouteQuery(to.query)
      this.fetchDashboardData()
    },
    filterUserCountries: function () {
      this.$nextTick(function () {
        this.initializeFilterDropdowns()
      })
    },
    sellerAccountsList: function () {
      this.$nextTick(function () {
        this.initializeFilterDropdowns()
      })
    }
  }
}
</script>

<style scoped>
.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #8b949e;
}

.loading-state i {
  font-size: 48px;
  color: #58a6ff;
  margin-bottom: 16px;
}

.error-state {
  text-align: center;
  padding: 48px;
  background: rgba(248, 81, 73, 0.1);
  border: 1px solid #f85149;
  border-radius: 12px;
  margin: 24px;
}

.error-state i {
  font-size: 48px;
  color: #f85149;
}

.error-state h3 {
  color: #f85149;
  margin: 16px 0 8px;
}

.error-state p {
  color: #8b949e;
  margin-bottom: 16px;
}

.dashboard-tabs {
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
}

.tab-button {
  padding: 12px 24px;
  background: transparent;
  border: 1px solid #30363d;
  border-radius: 6px;
  color: #8b949e;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-button:hover {
  border-color: #58a6ff;
  color: #c9d1d9;
}

.tab-button.active {
  background: rgba(88, 166, 255, 0.1);
  border-color: #58a6ff;
  color: #58a6ff;
}

@media (max-width: 768px) {
  .dashboard-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>



// WEBPACK FOOTER //
// BuyBoxDashboard.vue?1ad9faaa